// src/components/notify/NotificationBell.js
// Navbar bell: unread count, pulsing red dot for unread critical / pending acknowledgements,
// opens the FlowSense notification centre. Requires <NotifyProvider> above it.
import React, { memo } from 'react';
import { Box, IconButton, Tooltip, useColorModeValue } from '@chakra-ui/react';
// --- UPDATE (build fix): `keyframes` is not exported by this project's @chakra-ui/react version —
// it comes from @emotion/react (already installed as a Chakra peer dependency).
import { keyframes } from '@emotion/react';
// --- END UPDATE
import { MdNotifications, MdNotificationsActive } from 'react-icons/md';
import { useNotify } from './NotifyProvider';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(229, 62, 62, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

function NotificationBell({ size = 'md', variant = 'ghost', ...rest }) {
    const { summary, openCenter, status } = useNotify();
    const badgeBorder = useColorModeValue('white', 'gray.800');
    const unread = summary?.unread || 0;
    const urgent = (summary?.unread_critical || 0) + (summary?.ack_pending || 0);
    const label = `Notifications${unread ? `, ${unread} unread` : ''}${summary?.ack_pending ? `, ${summary.ack_pending} need acknowledgement` : ''}`;
    const offline = status === 'offline' || status === 'reconnecting';
    return (
        <Tooltip label={offline ? `${label} · reconnecting…` : label} hasArrow openDelay={300}>
            <Box position="relative" display="inline-flex">
                <IconButton
                    aria-label={label}
                    icon={urgent ? <MdNotificationsActive /> : <MdNotifications />}
                    variant={variant}
                    size={size}
                    fontSize="xl"
                    onClick={openCenter}
                    color={urgent ? 'red.500' : undefined}
                    {...rest}
                />
                {unread > 0 && (
                    <Box
                        as="span"
                        position="absolute"
                        top="2px"
                        right="2px"
                        minW="18px"
                        h="18px"
                        px="5px"
                        borderRadius="full"
                        bg={urgent ? 'red.500' : 'purple.500'}
                        color="white"
                        fontSize="10px"
                        fontWeight="800"
                        lineHeight="18px"
                        textAlign="center"
                        borderWidth="2px"
                        borderColor={badgeBorder}
                        animation={urgent ? `${pulse} 1.8s infinite` : undefined}
                        pointerEvents="none"
                        data-testid="notify-badge"
                    >
                        {unread > 99 ? '99+' : unread}
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}

export default memo(NotificationBell);